import { AuditLogResponse, FieldChangeDisplay } from '../../audit/models/audit.model';
import { TicketAuditLogResponse } from '../models/tickets.model';

/**
 * Adapta el audit log PROPIO de tickets (`GET /api/tickets/{id}/audit`, tabla
 * `ticket_audit_log`) a la forma `AuditLogResponse` que consume el componente
 * compartido <app-audit-timeline>, para homologar la línea de tiempo del ticket
 * con la del resto de módulos (contactos, negocios, empresas, leads, tareas...).
 *
 * NO se migra a la auditoría central (`audit_log`): esa tabla sólo registra
 * INSERT/UPDATE/DELETE/SEND_EMAIL/REOPEN del ticket, mientras que el log propio
 * registra además SLA_ALERT, STATUS_CHANGE, STAGE_CHANGE, RESOLVE, COMMENT*,
 * FOLLOWER_*, MERGE y SPLIT. Cambiar de fuente perdería esos eventos.
 *
 * Diferencia de granularidad: el DTO de tickets es a nivel de CAMPO (un registro
 * por cambio) y `AuditLogResponse` es a nivel de REGISTRO (un registro con N
 * cambios). El mapeo es 1:1 — cada entrada de ticket produce un log con, a lo
 * sumo, una fila de diff.
 *
 * Reglas de presentación (ver `AuditDisplayService.getTimelineHeadline`, cuya
 * prioridad es description → etiqueta de subtipo → título por entidad/operación):
 *
 *  - NARRATIVOS PUROS (`NARRATIVE_ONLY_TYPES`): se envía `description` con el
 *    texto literal que ya mostraba la línea de tiempo a medida, y
 *    `changesDisplay: []` — su frase ya contiene el único valor de la entrada,
 *    así que una tabla "Antes/Después" sólo repetiría el titular.
 *    Ej.: SPLIT → "Ticket dividido: TKT-2026-00034-01, TKT-2026-00034-02".
 *
 *  - CAMBIO DE CAMPO (STATUS_CHANGE, STAGE_CHANGE, PIPELINE_CHANGE, UPDATE,
 *    DELETE y cualquier tipo desconocido): sin `description`; el titular sale de
 *    la etiqueta del subtipo ("Cambio de estado") y la transición viaja en
 *    `changesDisplay`, que el componente compartido ya renderiza como resumen
 *    ("Estado: Abierto → Cerrado"), chip del campo y tabla Antes/Después. Es
 *    exactamente el comportamiento de contactos/negocios.
 *
 *  - MIXTOS (ASSIGN, RESOLVE, REOPEN, COMMENT, COMMENT_EDIT,
 *    COMMENT_DELETE): frase + fila de diff. Su titular es narrativo pero la
 *    entrada SÍ trae una transición real que se perdería con `[]`: RESOLVE y
 *    REOPEN mueven `id_ticket_status` (Nuevo → Resuelto), COMMENT* llevan el
 *    texto del comentario en old/newValue y ASSIGN trae el responsable anterior
 *    — dato que el titular a medida descartaba.
 *
 * El backend ya envía las etiquetas enriquecidas en español (`fieldLabel`,
 * `oldValueLabel`, `newValueLabel`, `actorName`): aquí NO se re-traducen, sólo
 * se reubican. Lo único que se traduce en cliente es el `changeType`, que el
 * backend no envía localizado (igual que hacía `auditTitle()`).
 */

/** changeType → etiqueta en español para el badge de subtipo. */
const CHANGE_TYPE_LABELS: Record<string, string> = {
  STATUS_CHANGE: 'Cambio de estado',
  STAGE_CHANGE: 'Cambio de etapa',
  PIPELINE_CHANGE: 'Cambio de pipeline',
  ASSIGN: 'Asignación',
  RESOLVE: 'Resolución',
  REOPEN: 'Reapertura',
  SLA_ALERT: 'Alerta de SLA',
  COMMENT: 'Comentario agregado',
  COMMENT_EDIT: 'Comentario editado',
  COMMENT_DELETE: 'Comentario eliminado',
  FOLLOWER_ADD: 'Seguidor agregado',
  FOLLOWER_REMOVE: 'Seguidor eliminado',
  MERGE: 'Fusión',
  SPLIT: 'División',
  SUB_TICKET: 'Subticket creado',
  ARCHIVE: 'Archivado',
  UNARCHIVE: 'Desarchivado',
};

/**
 * Tipos SIN tabla "Antes/Después": su frase ya contiene el único valor no vacío
 * de la entrada (verificado contra los datos reales de `ticket_audit_log`), así
 * que una fila de diff sólo repetiría el titular.
 *
 * OJO: aquí NO entran RESOLVE, REOPEN ni COMMENT*. Aunque su titular sea una
 * frase, esas entradas SÍ llevan una transición real que se perdería:
 * RESOLVE/REOPEN mueven `id_ticket_status` (Nuevo → Resuelto) y COMMENT* llevan
 * el texto del comentario en old/newValue. Esos tipos son mixtos: frase + diff.
 *
 * ARCHIVE/UNARCHIVE sí entran desde V180: su transición es `is_archived`
 * false → true, que en la tabla Antes/Después se leería como "Archivado:
 * false → true" — puro ruido junto al titular "Ticket archivado". (Antes de V180
 * ARCHIVE movía el estado del ticket, y por eso estaba fuera de esta lista.)
 */
const NARRATIVE_ONLY_TYPES = new Set([
  'CREATE',
  'SLA_ALERT',
  'SPLIT',
  'MERGE',
  'ARCHIVE',
  'UNARCHIVE',
  // Inc 74: la entrada sólo trae el número del subticket en newValue — la frase ya
  // lo contiene, igual que SPLIT/MERGE.
  'SUB_TICKET',
  'FOLLOWER_ADD',
  'FOLLOWER_REMOVE',
]);

/** CREATE/INSERT → CREATE; bajas → DELETE; el resto → UPDATE (bucket de color del dot). */
function operationTypeOf(changeType: string): 'CREATE' | 'UPDATE' | 'DELETE' {
  switch (changeType) {
    case 'CREATE':
    case 'INSERT':
      return 'CREATE';
    case 'DELETE':
    case 'REMOVE':
    case 'ARCHIVE':
    case 'COMMENT_DELETE':
    case 'FOLLOWER_REMOVE':
      return 'DELETE';
    default:
      return 'UPDATE';
  }
}

/**
 * Frase legible en español de una entrada narrativa. Conserva literalmente los
 * textos de la línea de tiempo a medida previa (INC 20 / INC 67).
 */
function narrativeDescription(
  changeType: string,
  oldL: string,
  newL: string
): string | undefined {
  switch (changeType) {
    case 'CREATE':
      return newL ? `Ticket creado (${newL})` : 'Ticket creado';
    case 'ASSIGN':
      return newL ? `Asignado a ${newL}` : 'Asignación actualizada';
    // El valor viaja en la fila de diff (Estado: Nuevo → Resuelto), así que el
    // titular no lo repite — antes decía "Ticket resuelto: Resuelto".
    case 'RESOLVE':
      return 'Ticket resuelto';
    case 'REOPEN':
      return 'Ticket reabierto';
    case 'SLA_ALERT':
      return newL ? `Alerta de SLA: ${newL}` : 'Alerta de SLA';
    case 'COMMENT':
      return 'Comentario agregado';
    case 'COMMENT_EDIT':
      return 'Comentario editado';
    case 'COMMENT_DELETE':
      return 'Comentario eliminado';
    case 'FOLLOWER_ADD':
      return newL ? `Seguidor agregado: ${newL}` : 'Seguidor agregado';
    case 'FOLLOWER_REMOVE':
      return oldL ? `Seguidor eliminado: ${oldL}` : 'Seguidor eliminado';
    case 'MERGE':
      return newL ? `Ticket fusionado: ${newL}` : 'Ticket fusionado';
    // INC 67: el asiento de división lista los tickets resultantes.
    case 'SPLIT':
      return newL ? `Ticket dividido: ${newL}` : 'Ticket dividido';
    // Inc 74: se registra en el historial del ticket PADRE con el número del hijo.
    case 'SUB_TICKET':
      return newL ? `Subticket creado: ${newL}` : 'Subticket creado';
    case 'ARCHIVE':
      return 'Ticket archivado';
    case 'UNARCHIVE':
      return 'Ticket desarchivado';
    default:
      return undefined;
  }
}

/** Fila de diff (una sola) para los eventos que sí son un cambio de campo. */
function diffRowsOf(
  entry: TicketAuditLogResponse,
  oldL: string,
  newL: string
): FieldChangeDisplay[] {
  if (!oldL && !newL) return [];
  return [
    {
      field: entry.fieldName || 'value',
      label: (entry.fieldLabel && entry.fieldLabel.trim()) || entry.fieldName || 'Campo',
      oldValue: entry.oldValue,
      newValue: entry.newValue,
      oldDisplay: oldL || null,
      newDisplay: newL || null,
    },
  ];
}

/** Adapta una entrada del audit log de tickets al DTO del timeline compartido. */
export function toAuditLogResponse(entry: TicketAuditLogResponse): AuditLogResponse {
  const changeType = (entry.changeType || '').toUpperCase();
  const oldL = (entry.oldValueLabel ?? entry.oldValue ?? '').toString().trim();
  const newL = (entry.newValueLabel ?? entry.newValue ?? '').toString().trim();
  const actor = (entry.actorName && entry.actorName.trim()) || 'Sistema';

  const narrativeOnly = NARRATIVE_ONLY_TYPES.has(changeType);
  const description = narrativeDescription(changeType, oldL, newL);

  return {
    id: entry.id,
    entityName: 'ticket',
    idEntity: String(entry.idTicket),
    moduleName: 'ticket',
    operationType: operationTypeOf(changeType),
    // Los tipos CRUD puros no aportan subtipo (el titular por entidad ya basta).
    operationSubtype:
      changeType && !['CREATE', 'UPDATE', 'DELETE'].includes(changeType) ? changeType : undefined,
    operationSubtypeLabel: CHANGE_TYPE_LABELS[changeType] ?? null,
    description,
    // `[]` = sin diff mostrable → el compartido no pinta tabla ni chips.
    changesDisplay: narrativeOnly ? [] : diffRowsOf(entry, oldL, newL),
    idUser: entry.idUserActor ?? 0,
    username: actor,
    userFullName: actor,
    businessId: 0,
    timestamp: entry.datetimeCreated,
    severity: changeType === 'SLA_ALERT' ? 'WARN' : 'INFO',
    success: true,
  };
}

/** Adapta la lista completa devuelta por `GET /api/tickets/{id}/audit`. */
export function toAuditLogResponses(entries: TicketAuditLogResponse[]): AuditLogResponse[] {
  return (entries ?? []).map(toAuditLogResponse);
}
